import { api } from "@convex/_generated/api";
import { Button } from "@buddy/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useAction } from "convex/react";
import { useEffect, useRef, useState } from "react";
import { SignedIn } from "@/features/auth/signed-in";
import { errorText } from "@/features/settings/page";
import { clearPending, peekPending } from "@/lib/openrouter";

// Where OpenRouter's sign-in comes back to, with ?code and ?state. The verifier this tab
// kept is sent with them to Convex, which trades them for the org's key; the key never
// comes back here. Then the page returns to wherever the sign-in started.

export const Route = createFileRoute("/connect/openrouter")({
  validateSearch: (search: Record<string, unknown>): { code?: string; state?: string } => ({
    ...(typeof search.code === "string" ? { code: search.code } : {}),
    ...(typeof search.state === "string" ? { state: search.state } : {}),
  }),
  component: Page,
});

function Page() {
  return (
    <SignedIn>
      <Finish />
    </SignedIn>
  );
}

const NOT_STARTED = "This sign-in didn't start in this tab, or OpenRouter sent no code. Go back and connect again.";

function Finish() {
  const { code, state } = Route.useSearch();
  const exchange = useAction(api.openrouter.exchange);
  const router = useRouter();
  const [pending] = useState(() => (state === undefined ? null : peekPending(sessionStorage, state)));
  const [failed, setFailed] = useState<string | null>(null);
  const error = code === undefined || pending === null ? NOT_STARTED : failed;
  const returnTo = pending?.returnTo ?? "/settings/connections";
  // React runs effects twice in development; a code can be traded only once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current || code === undefined || pending === null) return;
    started.current = true;
    exchange({ orgId: pending.orgId, code, codeVerifier: pending.verifier, state: pending.state }).then(
      () => {
        clearPending(sessionStorage);
        router.history.replace(pending.returnTo);
      },
      (failure: unknown) => {
        clearPending(sessionStorage);
        setFailed(errorText(failure));
      },
    );
  }, [code, pending, exchange, router]);

  return (
    <div className="flex h-full items-center justify-center bg-app p-6">
      <div className="flex w-full max-w-[420px] flex-col items-start gap-3 rounded-lg bg-surface-2 p-5 shadow-ring">
        <span className="text-[15px] font-medium leading-5 text-text">
          {error === null ? "Connecting OpenRouter…" : "OpenRouter isn't connected"}
        </span>
        <p className="text-[13px] leading-[18px] text-text-2">
          {error ?? "Finishing the sign-in with OpenRouter. This takes a moment."}
        </p>
        {error !== null && (
          <Button size="sm" onClick={() => router.history.push(returnTo)}>
            Go back
          </Button>
        )}
      </div>
    </div>
  );
}
